import * as d3 from "d3";

export function CustomHistogram( dataValues, {
  width = 300,                                          // Width of the graph (margins excluded)
  height = 200,                                         // Height of the graph (margins excluded)
  margin = {top:70, bottom:40, left:70, right:40},      // Empty space around the rendered graph (inside <svg>)
  legendRectWidth = 60,                                 // Legend rectangle width
  legendRectHeight = 20,                                // Legend rectangle height
  legendRectMarginRight = 15,                           // Legend rectangle margin right
  legendLabelMarginTop = 15,                            // Legend rectangle margin top
  labelTextSize = "15px",                               // Font size of X axis and legend labels (applied only to string labels)
  numericLabelTextSize = "12px",                        // Font size of numeric X axis labels (only for computing container size offsets)
  barColor = "#97cdf2",                                 // Color of the graph bars (turquoise default)
  barLineColor = "#43a8ec",                             // Color of the graph bars outline
  barLegend = "Variable name",                          // Legend label
  xAxisSteps = null,                                    // Defines value differences of bars on X axis (exclusive with bars)
  xAxisBars = null,                                     // Defines number of bars generated (exclusive with steps)
  xAxisMin = null,                                      // Defines minimum value on X axis
  xAxisMax = null,                                      // Defines maximum value on X axis
  xAxisPrecision = 2,                                   // Precision of numbers on X axis, when applying steps/bars
  isStringData = false                                  // if true, data value occurrences are displayed as output instead of histogram
}) {
    // DEFINITION PART
    // --------------------

    // common variable definition
    var bins;
    var domainX;
    var maxLabelLengthX = 0;
    var domainMaxY;

    // This fork defines how data is processed
    // String data will get their occurrences counted (output = number of occurrences for each value)
    // Numeric data will go through histogram processing instead (output = histogram bins)
    if (isStringData) {
        bins = new Map();
        for (let i = 0; i < dataValues.length; i++) {
            // increment existing key by +1, or init new key to 1
            bins.set(dataValues[i], bins.get(dataValues[i]) + 1 || 1);
        }
        var binKeys = Array.from(bins.keys());

        // get X axis domain (unique string data values),
        // then get max length of X axis labels (label rotation + container size adjusted when too long)
        domainX = binKeys.map(d => barLegend + "-" + d);
        for (let i = 0; i < domainX.length; i++) {
            maxLabelLengthX = Math.max(maxLabelLengthX, getLabelWidth(domainX[i], labelTextSize));
        }

        // get Y axis domain max value (greatest number of occurrences)
        domainMaxY = d3.max(Array.from(bins.values()));

    } else {
        // get min/max values in the dataset, capped by the input argument
        var minVal = d3.min(dataValues);
        if (xAxisMin) minVal = (xAxisMin > minVal) ? xAxisMin : minVal;
        var maxVal = d3.max(dataValues);
        if (xAxisMax) maxVal = (xAxisMax < maxVal) ? xAxisMax : maxVal;

        // create definition of histogram data distribution into bins (= rendered bars)
        var histogram = d3.histogram()
           .domain([minVal, maxVal])            // interval to divide

        // adjust number of bars by steps/bars from input arguments
        if (xAxisSteps) {
            histogram.thresholds(getThresholdsBySteps(xAxisSteps, minVal, maxVal, xAxisPrecision));
        } else if (xAxisBars) {
            histogram.thresholds(getThresholdsByBars(xAxisBars, minVal, maxVal, xAxisPrecision));
        }

        // get bin intervals and number of values in them
        bins = histogram(dataValues);

        // get X axis domain (left border value of each bin),
        // then get max length of X axis labels (label rotation + container size adjusted when too long)
        domainX = d3.map(bins, function(d) {return d.x0;});
        for (let i = 0; i < domainX.length; i++) {
            maxLabelLengthX = Math.max(maxLabelLengthX, getLabelWidth(domainX[i], numericLabelTextSize));
        }

        // get Y axis domain max value (greatest number of occurrences, for unique string data values)
        domainMaxY = d3.max(bins, function(d) {return d.length;});
    }

    // create definition of Y axis properties
    var yScale = d3.scaleLinear()
       .domain([0, domainMaxY])                // domain of input values, to be mapped to graph proportions
       .nice()                                 // round domain so that generated Y axis always includes max domain value
       .range([height, 0]);                    // length of rendered axis (d3.axisLeft() draws from above to bottom)

    // create definition of X axis properties
    var xScale = d3.scaleBand()
       .domain(domainX)                         // domain of input values, to be mapped to graph proportions
       .range([0, width])                       // length of rendered axis
       .padding(0.2);

    // if X labels bigger then bar width, adjust container size to make space for rotated label
    // label gets rotated by 45 degrees, so use pythagoras theorem to get necessary offset
    if (maxLabelLengthX > xScale.bandwidth()){
        margin.bottom += Math.sqrt((maxLabelLengthX * maxLabelLengthX) /2);
    }

    // create drawing area, big enough to include graph margins
    const svg = d3.create("svg")
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom);

    // create group to contain all elements of the graph (easier positioning)
    const mainGroup = svg.append("g")
        .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    // create legend rectangle and label nodes
    const legendRect = d3.create("rect")
       .attr("x", 0)
       .attr("y", 0)
       .attr("width", legendRectWidth)
       .attr("height", legendRectHeight)
       .style("fill", barColor)
       .attr("opacity", "80%")
       .style("stroke", barLineColor)
       .attr('stroke-width', 2);
    const legendLabel = d3.create("text")
       .attr("transform", "translate("+(legendRectWidth + legendRectMarginRight)+", " +legendLabelMarginTop+ ")")
       .attr("font-size", labelTextSize)
       .text(barLegend);
    const totalLegendWidth = legendRectWidth + legendRectMarginRight + getLabelWidth(barLegend, labelTextSize);

    // RENDERING PART
    // --------------------

    // render grid lines
    mainGroup.selectAll("line.horizontalGrid")
        .data(yScale.ticks())
        .enter()
        .append("line")
            .attr("class", "horizontalGrid")
            .attr("x1", 0)
            .attr("x2", width)
            .attr("y1", function(d){ return yScale(d);})
            .attr("y2", function(d){ return yScale(d);})
            .attr("stroke", "black")
            .attr("stroke-opacity", "20%");

    // render bars
    // calls are adjusted depending on type of processed data
    mainGroup.selectAll("rect")
       .data((isStringData) ? binKeys : bins)
       .enter()
       .append("rect")      // rendered bar definition (created whenever there is more data than rendered selection elements)
          .attr("transform", function(d) { return "translate(" + xScale((isStringData) ? (barLegend + "-" + d) : d.x0) + "," + yScale( (isStringData) ? bins.get(d) : d.length) + ")"; })
          .attr("width", xScale.bandwidth())
          .attr("height", function(d) { return height - yScale((isStringData) ? bins.get(d) : d.length); })
          .style("fill", barColor)
          .attr("opacity", "80%")
          .style("stroke", barLineColor)
          .attr('stroke-width', 2);

    // render X axis (overwrite bottom outline of bars)
    const xlabelGroup = mainGroup.append("g")                    // add new group of elements to main group
         .attr("transform", "translate(0," + height + ")")       // positioning of elements in the group
         .call(d3.axisBottom(xScale))                            // in current selection, call function that renders the X axis

    // if X axis contains string labels, enlarge font
    // also if X labels too long, rotate them by 45 degrees
    if (isStringData){
        xlabelGroup.selectAll("text").attr("font-size", labelTextSize);
    }
    if (maxLabelLengthX > xScale.bandwidth()){
        xlabelGroup.selectAll("text")
            .style("text-anchor", "end")
            .attr("dx", "-.8em")
            .attr("dy", ".15em")
            .attr("transform", "rotate(-45)");
    }

    // render Y axis to graph
    mainGroup.append("g")
       .call(d3.axisLeft(yScale));

    // render Y axis label
    svg.append("text")
        .attr("transform", "rotate(-90 "+(margin.left/2)+" "+(height/2 + margin.top)+"), translate(0," + (height/2 + margin.top ) + ")")
        .attr("dy", "-.3em")
        .attr("font-size", labelTextSize)
        .text("Frequency");

    // render bar legend (rectangle and label)
    svg.append("g")
       .attr("transform", "translate("+ ((margin.left + width + margin.right)/2 - totalLegendWidth/2) +"," + (margin.top/2 - legendRectHeight/2) + ")")
       .append(function() {return legendRect.node()});
    svg.append("g")
       .attr("transform", "translate("+ ((margin.left + width + margin.right)/2 - totalLegendWidth/2) +"," + (margin.top/2 - legendRectHeight/2) + ")")
       .append(function() {return legendLabel.node()});

    return svg.node();
}

// helper function, to get label text width
function getLabelWidth(text, fontSize) {
    var dummySvg = d3.select("body")
        .append("svg");
    dummySvg.append("text")
        .attr("x", -100000)
        .attr("y", -100000)
        .attr("font-size", fontSize)
        .text(text);
    const size = dummySvg.node().getBBox();
    dummySvg.remove();
    return size.width;
}

function getThresholdsBySteps(stepOffset, minVal, maxVal, precision) {
    var thresholdVals = [];
    const barCnt = (maxVal - minVal) / stepOffset;
    for (let i = 0; i < barCnt; i++) {
      thresholdVals[i] = Number.parseFloat((minVal + stepOffset * i).toFixed(precision));
    }
    return thresholdVals;
}

function getThresholdsByBars(barsCount, minVal, maxVal, precision) {
    var thresholdVals = [];
    const offset = (maxVal - minVal) / barsCount;
    for (let i = 0; i < barsCount; i++) {
      thresholdVals[i] = Number.parseFloat((minVal + offset * i).toFixed(precision));
    }
    return thresholdVals;
}
